import { API_BASE_URL } from "../apiConfig";
import React, { useCallback, useEffect, useState } from "react";
import "./RecyclingDashboard.css";

const itemIcons = {
  Plastic: "PL",
  Cardboard: "CB",
  Paper: "PA",
  "Metal Can": "MC",
  Glass: "GL",
};

const getIcon = (name) => itemIcons[name] || "RC";

const formatDate = (d) =>
  new Date(d).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const RecyclingNotifications = () => {
  const [companyId, setCompanyId] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collecting, setCollecting] = useState({});
  const [toast, setToast] = useState(null);

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem("currentUser");
      if (!stored) return;

      const parsed = JSON.parse(stored);
      if (parsed.role === "recycling_company") setCompanyId(parsed._id || null);
    } catch (e) {
      console.error("Failed to read recycling company session:", e);
    }
  }, []);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchNotifications = useCallback(async () => {
    if (!companyId) return;

    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/recycling/notifications/${companyId}`);
      const data = await res.json();
      setNotifications(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching notifications:", err);
      showToast("Failed to load notifications", "error");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (companyId) fetchNotifications();
    else setLoading(false);
  }, [companyId, fetchNotifications]);

  const handleCollect = async (notifId) => {
    setCollecting((prev) => ({ ...prev, [notifId]: true }));
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/recycling/notifications/collect/${notifId}`,
        { method: "PUT" }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to update collection");

      showToast("Items marked as collected.", "success");
      fetchNotifications();
    } catch (err) {
      showToast(err.message || "Failed to update", "error");
    } finally {
      setCollecting((prev) => ({ ...prev, [notifId]: false }));
    }
  };

  const pendingCount = notifications.filter((n) => n.status === "pending").length;

  return (
    <div className="rcd-page">
      {toast && <div className={`rcd-toast rcd-toast-${toast.type}`}>{toast.msg}</div>}

      <div className="rcd-topbar">
        <div>
          <h1>Notifications</h1>
          <p>Incoming recycling pickup requests assigned by admin.</p>
        </div>
      </div>

      {!companyId ? (
        <div className="rcd-no-id">Company ID not found. Please log out and log in again.</div>
      ) : loading ? (
        <div className="rcd-loading">Loading notifications...</div>
      ) : (
        <div className="rcd-panel">
          <div className="rcd-panel-header orange-header">
            <span>IN</span>
            <h2>Incoming Notifications</h2>
            <span className="rcd-badge orange-badge">{pendingCount}</span>
          </div>

          {notifications.length === 0 ? (
            <div className="rcd-empty">
              <span>--</span>
              <p>No notifications received yet.</p>
            </div>
          ) : (
            <div className="rcd-notif-list">
              {notifications.map((notification) => (
                <div
                  key={notification._id}
                  className={`rcd-notif-card ${
                    notification.status === "collected" ? "collected" : "pending"
                  }`}
                >
                  <div className="rcd-notif-top">
                    <span className="rcd-notif-icon">{getIcon(notification.itemType)}</span>
                    <div className="rcd-notif-details">
                      <span className="rcd-notif-type">{notification.itemType}</span>
                      <span className="rcd-notif-qty">Quantity: {notification.quantity}</span>
                      <span className="rcd-notif-loc">
                        Location: {notification.location?.lat?.toFixed(4)},{" "}
                        {notification.location?.lng?.toFixed(4)}
                      </span>
                      <span className="rcd-notif-date">{formatDate(notification.createdAt)}</span>
                    </div>
                    <span className={`rcd-notif-pill ${notification.status}`}>
                      {notification.status === "collected" ? "Collected" : "Pending"}
                    </span>
                  </div>
                  {notification.status === "pending" && (
                    <button
                      className="rcd-collect-btn"
                      onClick={() => handleCollect(notification._id)}
                      disabled={collecting[notification._id]}
                    >
                      {collecting[notification._id] ? "Updating..." : "Mark as Collected"}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RecyclingNotifications;
